import * as T from 'three';
/** Small depth-tested NPC badge; authored responses appear only after a real interaction. */
export function addNpcBadge(avatar, resident, makeCanvas=()=>document.createElement('canvas')) {
  if(resident?.kind!=='npc')return avatar;
  const canvas=makeCanvas();canvas.width=768;canvas.height=192;
  const ctx=canvas.getContext('2d');if(!ctx)return avatar;
  const texture=new T.CanvasTexture(canvas);texture.colorSpace=T.SRGBColorSpace;
  const material=new T.SpriteMaterial({map:texture,transparent:true,depthTest:true,depthWrite:false,toneMapped:false});
  const badge=new T.Sprite(material);badge.name='Scripted resident · NPC';avatar.root.add(badge);
  let remaining=0,disposed=false,visibility=1,previousVisibility=1,social=null,spokenText='',shownText='';
  const gestureBones=new Map();
  const originals=new WeakMap();
  avatar.setPresence=value=>{visibility=Number.isFinite(value)?Math.max(0,Math.min(1,value)):1;};
  function applyVisibility(){
    avatar.root.visible=visibility>0;
    if(visibility===1&&previousVisibility===1)return;
    previousVisibility=visibility;
    avatar.root.traverse(object=>{
      for(const material of object.material?(Array.isArray(object.material)?object.material:[object.material]):[]){
        if(!originals.has(material))originals.set(material,{opacity:material.opacity,transparent:material.transparent,depthWrite:material.depthWrite});
        const original=originals.get(material),transparent=visibility<1||original.transparent;
        if(material.transparent!==transparent){material.transparent=transparent;material.needsUpdate=true;}
        material.opacity=original.opacity*visibility;material.depthWrite=visibility<1?false:original.depthWrite;
      }
    });
  }
  function draw(text='') {
    shownText=text;
    ctx.clearRect?.(0,0,768,192);ctx.fillStyle='rgba(22,27,43,.84)';ctx.beginPath();ctx.roundRect(8,8,752,176,28);ctx.fill();
    ctx.textAlign='center';ctx.textBaseline='middle';ctx.font=text?'500 27px system-ui, sans-serif':'500 64px system-ui, sans-serif';
    ctx.fillStyle='#e4e8ef';ctx.fillText(`${resident.name} · NPC`,384,text?39:99);
    if(text){ctx.font='30px system-ui, sans-serif';const words=text.split(' ');let line='',lines=[];for(const word of words){if((line+' '+word).length>43){lines.push(line);line=word;}else line+=(line?' ':'')+word;}if(line)lines.push(line);lines.slice(0,3).forEach((value,i)=>ctx.fillText(value,384,79+i*34));}
    badge.position.y=text?2.48:2.12;badge.scale.set(text?4:1.65,text?1:.413,1);texture.needsUpdate=true;
  }
  draw();
  avatar.speak=text=>{if(!disposed){remaining=7;spokenText=text;draw(text);}};
  avatar.setSocial=value=>{social=value&&typeof value==='object'?value:null;};
  function restoreGestures(){for(const [bone,state]of gestureBones)if(state.applied){bone.quaternion.copy(state.base);state.applied=false;}}
  function gesture(name,axis,angle){
    const bone=avatar.root.getObjectByName(name);if(!bone?.isBone)return;
    let state=gestureBones.get(bone);if(!state){state={base:bone.quaternion.clone(),applied:false};gestureBones.set(bone,state);}
    state.base.copy(bone.quaternion);state.applied=true;bone[axis](angle);
  }
  function updateSocial(speed=0){
    const utterance=social?.utterance;
    const ambient=utterance?.authored===true&&typeof utterance.text==='string'&&utterance.text.length<=180?utterance.text:'';
    const text=remaining>0?spokenText:ambient;if(text!==shownText)draw(text);
    const gestureKind=remaining>0&&speed<.2?'speaking':social?.gesture;
    const phase=remaining>0&&speed<.2?7-remaining:social?.phase;
    if(!['speaking','listening'].includes(gestureKind)||!Number.isFinite(phase))return;
    const speaking=gestureKind==='speaking';
    // Subtle additive head/hand/torso motion over the real idle clip. Restore
    // before the next mixer tick so offsets never accumulate or alter assets.
    const nod=speaking?Math.sin(phase*3.4)*.045:Math.sin(Math.min(1,(phase%5)/.9)*Math.PI)*.11;
    gesture('J_Bip_C_Head','rotateX',nod);
    if(speaking){gesture('J_Bip_R_LowerArm','rotateZ',Math.sin(phase*2.5)*.065);gesture('J_Bip_C_Spine','rotateY',Math.sin(phase*1.6)*.025);}
  }
  // An absent resident must not replay a pre-departure reply or additive pose.
  avatar.resetTransient=()=>{remaining=0;spokenText='';social=null;restoreGestures();if(shownText)draw();};
  const update=avatar.update?.bind(avatar),dispose=avatar.dispose.bind(avatar);
  avatar.update=(dt,...args)=>{restoreGestures();applyVisibility();if(remaining>0)remaining-=dt;update?.(dt,...args);updateSocial(args[1]??0);};
  avatar.dispose=()=>{if(disposed)return;disposed=true;restoreGestures();gestureBones.clear();badge.removeFromParent();texture.dispose();material.dispose();dispose();};
  return avatar;
}
